/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import FieldWithIcon from '../FieldWithIcon';
import { getApiBaseUrl } from '../../../../../../core/config/api';

interface PlayerProfileSectionProps {
  playerProfile: any;
  setPlayerProfile: any;
  canManagePremium: boolean;
  economyAction: any;
  setEconomyAction: any;
  economyAdjusting: boolean;
  savingProfile: boolean;
  adjustEconomy: (resource: 'argents' | 'medailles', operation: 'add' | 'remove') => Promise<void>;
  savePlayerProfile: () => Promise<void>;
  canResetAccount?: boolean;
}

const PlayerProfileSection: React.FC<PlayerProfileSectionProps> = ({
  playerProfile,
  setPlayerProfile,
  canManagePremium,
  economyAction,
  setEconomyAction,
  economyAdjusting,
  savingProfile,
  adjustEconomy,
  savePlayerProfile,
  canResetAccount = false,
}) => {
  const [resetStep, setResetStep] = useState<0 | 1 | 2>(0);
  const [resetting, setResetting] = useState(false);
  const [resetMsg, setResetMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleAdminReset = async () => {
    setResetting(true);
    setResetMsg(null);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const res = await fetch(
        `${getApiBaseUrl(true)}/panel/v1/moderation/player/${playerProfile.id}/reset-account`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Erreur reset');
      setResetMsg({ type: 'success', text: data.message || 'Compte réinitialisé.' });
      setResetStep(0);
    } catch (err: any) {
      setResetMsg({ type: 'error', text: err.message || 'Erreur serveur' });
    } finally {
      setResetting(false);
    }
  };

  return (
        <div className="mb-3 rounded border border-[#33456f] bg-[#101f44] p-3">
          <div className="mb-2 rounded border border-[#2b3a61] bg-[#0a1329] p-2">
            <div className="text-sm font-semibold text-white">
              Joueur ciblé : {playerProfile.username} (ID #{playerProfile.id})
            </div>
            <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-300">
              <span>Email : {playerProfile.email}</span>
              <span>Activé : {Number(playerProfile.activated) ? 'Oui' : 'Non'}</span>
            </div>
          </div>
          <div className="mb-2 text-sm font-semibold text-white">
            Profil joueur {canManagePremium ? '(économie / statut / progression)' : '(modération — sans économie ni premium)'}
          </div>
          {!canManagePremium ? (
            <p className="mb-2 text-xs text-amber-200/90">
              Premium, argents, médailles et XP : réservés au CM et aux administrateurs. Utilisez la modération pour
              activer/désactiver le compte.
            </p>
          ) : null}
          <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
            <FieldWithIcon
              icon="🆔"
              label="ID joueur"
              value={String(playerProfile.id || '')}
              onChange={() => {}}
              placeholder="ID"
              className="opacity-70"
              readOnly
            />
            <FieldWithIcon
              icon="👤"
              label="Pseudo"
              value={playerProfile.username || ''}
              onChange={(v) => setPlayerProfile((p: any) => (p ? { ...p, username: v } : p))}
              placeholder="Username"
            />
            <FieldWithIcon
              icon="✉️"
              label="Email"
              value={playerProfile.email || ''}
              onChange={(v) => setPlayerProfile((p: any) => (p ? { ...p, email: v } : p))}
              placeholder="Email"
            />
            <FieldWithIcon
              icon="💶"
              label="Argent"
              value={String(playerProfile.argents ?? 0)}
              onChange={() => {}}
              placeholder="Argent"
              className="opacity-70"
              readOnly
            />
            <FieldWithIcon
              icon="🏅"
              label="Médailles"
              value={String(playerProfile.medailles ?? 0)}
              onChange={() => {}}
              placeholder="Médailles"
              className="opacity-70"
              readOnly
            />
            <FieldWithIcon
              icon="✅"
              label="Activé"
              value={String(playerProfile.activated ?? 1)}
              onChange={(v) => setPlayerProfile((p: any) => (p ? { ...p, activated: Number(v || 0) } : p))}
              placeholder="Activé (0/1)"
            />
            {canManagePremium && Number(playerProfile.supports_premium || 0) ? (
              <label className="flex items-center gap-2 rounded border border-[#33456f] bg-[#0a1329] px-2 py-1 text-xs text-slate-100">
                <span className="text-sm">⭐</span>
                <span className="shrink-0 text-[11px] text-slate-300">Premium</span>
                <select
                  value={String(Number(playerProfile.premium || 0))}
                  onChange={(e) => setPlayerProfile((p: any) => (p ? { ...p, premium: Number(e.target.value || 0) } : p))}
                  className="w-full bg-transparent text-xs text-slate-100 focus:outline-none"
                >
                  <option value="0">Non</option>
                  <option value="1">Oui</option>
                </select>
              </label>
            ) : null}
            {canManagePremium && Number(playerProfile.supports_xp || 0) ? (
              <FieldWithIcon
                icon="📈"
                label="XP"
                value={String(playerProfile.xp ?? 0)}
                onChange={(v) => setPlayerProfile((p: any) => (p ? { ...p, xp: Number(v || 0) } : p))}
                placeholder="XP"
              />
            ) : null}
          </div>
          {canManagePremium ? (
          <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
            <div className="rounded border border-[#33456f] bg-[#0a1329] p-2">
              <div className="mb-1 text-xs font-semibold text-white">Ajustement Argent</div>
              <div className="grid grid-cols-1 gap-1">
                <input
                  type="number"
                  min={1}
                  value={economyAction.argents.amount}
                  onChange={(e) => setEconomyAction((prev: any) => ({ ...prev, argents: { ...prev.argents, amount: e.target.value } }))}
                  placeholder="Montant"
                  className="rounded border border-[#33456f] bg-[#101f44] px-2 py-1 text-xs text-slate-100"
                />
                <input
                  value={economyAction.argents.reason}
                  onChange={(e) => setEconomyAction((prev: any) => ({ ...prev, argents: { ...prev.argents, reason: e.target.value } }))}
                  placeholder="Raison (optionnel, log audit)"
                  className="rounded border border-[#33456f] bg-[#101f44] px-2 py-1 text-xs text-slate-100"
                />
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={economyAdjusting}
                    onClick={() => void adjustEconomy('argents', 'add')}
                    className="rounded bg-emerald-700 px-2 py-1 text-[11px] text-white hover:bg-emerald-600 disabled:opacity-50"
                  >
                    + Ajouter
                  </button>
                  <button
                    type="button"
                    disabled={economyAdjusting}
                    onClick={() => void adjustEconomy('argents', 'remove')}
                    className="rounded bg-amber-700 px-2 py-1 text-[11px] text-white hover:bg-amber-600 disabled:opacity-50"
                  >
                    - Retirer
                  </button>
                </div>
              </div>
            </div>
            <div className="rounded border border-[#33456f] bg-[#0a1329] p-2">
              <div className="mb-1 text-xs font-semibold text-white">Ajustement Médailles</div>
              <div className="grid grid-cols-1 gap-1">
                <input
                  type="number"
                  min={1}
                  value={economyAction.medailles.amount}
                  onChange={(e) => setEconomyAction((prev: any) => ({ ...prev, medailles: { ...prev.medailles, amount: e.target.value } }))}
                  placeholder="Montant"
                  className="rounded border border-[#33456f] bg-[#101f44] px-2 py-1 text-xs text-slate-100"
                />
                <input
                  value={economyAction.medailles.reason}
                  onChange={(e) => setEconomyAction((prev: any) => ({ ...prev, medailles: { ...prev.medailles, reason: e.target.value } }))}
                  placeholder="Raison (optionnel, log audit)"
                  className="rounded border border-[#33456f] bg-[#101f44] px-2 py-1 text-xs text-slate-100"
                />
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={economyAdjusting}
                    onClick={() => void adjustEconomy('medailles', 'add')}
                    className="rounded bg-emerald-700 px-2 py-1 text-[11px] text-white hover:bg-emerald-600 disabled:opacity-50"
                  >
                    + Ajouter
                  </button>
                  <button
                    type="button"
                    disabled={economyAdjusting}
                    onClick={() => void adjustEconomy('medailles', 'remove')}
                    className="rounded bg-amber-700 px-2 py-1 text-[11px] text-white hover:bg-amber-600 disabled:opacity-50"
                  >
                    - Retirer
                  </button>
                </div>
              </div>
            </div>
          </div>
          ) : null}
          <div className="mt-2">
            <button
              type="button"
              onClick={() => void savePlayerProfile()}
              disabled={savingProfile}
              className="rounded bg-emerald-600 px-3 py-1 text-xs text-white hover:bg-emerald-500 disabled:opacity-50"
            >
              {savingProfile ? 'Sauvegarde...' : 'Sauvegarder profil joueur'}
            </button>
          </div>

          {canResetAccount && (
            <div className="mt-4 rounded border border-red-500/40 bg-red-950/30 p-3 space-y-2">
              <p className="text-xs font-semibold text-red-300">Reset de compte joueur</p>
              <p className="text-[11px] text-slate-400">
                Supprime bâtiments, véhicules, personnel et interventions. Remet argent (2M€), XP et niveau à 0.
                <span className="text-amber-400"> Médailles et premium conservés.</span>
              </p>
              {resetMsg && (
                <p className={`text-[11px] ${resetMsg.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                  {resetMsg.text}
                </p>
              )}
              {resetStep === 0 && (
                <button
                  type="button"
                  onClick={() => { setResetStep(1); setResetMsg(null); }}
                  className="rounded bg-red-700/60 border border-red-500/50 px-3 py-1 text-[11px] text-red-300 hover:bg-red-700 hover:text-white transition-colors"
                >
                  Réinitialiser ce compte
                </button>
              )}
              {resetStep === 1 && (
                <div className="flex gap-2 items-center">
                  <span className="text-[11px] text-amber-300">Confirmer le reset de {playerProfile.username} ?</span>
                  <button
                    type="button"
                    onClick={() => setResetStep(2)}
                    className="rounded bg-red-700 px-2 py-1 text-[11px] text-white hover:bg-red-600"
                  >Oui</button>
                  <button
                    type="button"
                    onClick={() => setResetStep(0)}
                    className="rounded bg-zinc-700 px-2 py-1 text-[11px] text-white hover:bg-zinc-600"
                  >Non</button>
                </div>
              )}
              {resetStep === 2 && (
                <div className="flex gap-2 items-center">
                  <span className="text-[11px] text-red-300 font-semibold">Action irréversible — confirmer ?</span>
                  <button
                    type="button"
                    disabled={resetting}
                    onClick={() => void handleAdminReset()}
                    className="rounded bg-red-600 px-2 py-1 text-[11px] text-white hover:bg-red-500 disabled:opacity-50"
                  >{resetting ? '...' : 'Confirmer'}</button>
                  <button
                    type="button"
                    onClick={() => setResetStep(0)}
                    className="rounded bg-zinc-700 px-2 py-1 text-[11px] text-white hover:bg-zinc-600"
                  >Annuler</button>
                </div>
              )}
            </div>
          )}
        </div>
  );
};

export default PlayerProfileSection;
